import React, { useCallback, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { analyticsAdminService } from '../../api/services/AnalyticsAdminService';
import type {
  AnalyticsAuthFilter,
  AnalyticsJourneyRow,
  AnalyticsOverviewViewModel,
  AnalyticsPagePerformanceRow,
  AnalyticsSessionDetailViewModel,
  AnalyticsSessionSummaryRow,
} from '../../api/types/analytics';

const TZ_LABEL = 'Europe/Istanbul (UTC+3 gösterim)';

const toDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildPresetRange = (daysBack: number): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - (daysBack - 1));
  return { from: toDateInputValue(from), to: toDateInputValue(today) };
};

const toIsoStartOfDay = (dateInput: string): string =>
  new Date(`${dateInput}T00:00:00`).toISOString();
const toIsoEndOfDay = (dateInput: string): string =>
  new Date(`${dateInput}T23:59:59.999`).toISOString();

const formatSeconds = (value: number): string => `${value.toFixed(1)} sn`;

const AnalyticsPage: React.FC = () => {
  const initialRange = useMemo(() => buildPresetRange(7), []);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [authFilter, setAuthFilter] = useState<AnalyticsAuthFilter>('All');
  const [pageTypeFilter, setPageTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [overview, setOverview] = useState<AnalyticsOverviewViewModel | null>(null);
  const [pages, setPages] = useState<AnalyticsPagePerformanceRow[]>([]);
  const [journeys, setJourneys] = useState<AnalyticsJourneyRow[]>([]);
  const [sessions, setSessions] = useState<AnalyticsSessionSummaryRow[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [selectedSession, setSelectedSession] = useState<AnalyticsSessionDetailViewModel | null>(
    null,
  );

  const rangeInvalid = from !== '' && to !== '' && from > to;

  const buildFilter = useCallback(
    () => ({
      from: toIsoStartOfDay(from),
      to: toIsoEndOfDay(to),
      authFilter,
      pageType: pageTypeFilter || undefined,
      journeyLength: 3,
      page: 1,
      pageSize: 50,
    }),
    [from, to, authFilter, pageTypeFilter],
  );

  const loadAll = useCallback(async () => {
    if (rangeInvalid || !from || !to) return;
    setLoading(true);
    setError(null);
    setSelectedSession(null);
    try {
      const filter = buildFilter();
      const [ov, pg, jn, sess] = await Promise.all([
        analyticsAdminService.getOverview(filter),
        analyticsAdminService.getPages(filter),
        analyticsAdminService.getJourneys(filter),
        analyticsAdminService.getSessions(filter),
      ]);
      setOverview(ov);
      setPages(pg);
      setJourneys(jn);
      setSessions(sess.items);
      setSessionTotal(sess.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analitik verileri yüklenemedi.');
      setOverview(null);
      setPages([]);
      setJourneys([]);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [buildFilter, from, to, rangeInvalid]);

  const openSession = useCallback(async (sessionId: string) => {
    try {
      const detail = await analyticsAdminService.getSessionDetail(sessionId);
      setSelectedSession(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Oturum detayı alınamadı.');
    }
  }, []);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">
          <i className="fas fa-chart-pie me-2 text-primary" />
          Analitik
        </h2>
        <span className="small text-muted">{TZ_LABEL}</span>
      </div>

      <p className="small text-muted">
        Etkileşim süreleri tahminidir; arka planda açık sekmeler sınırlı ölçülür. Ziyaretçi sayıları
        onaylı analitik tanımlayıcı ve oturum kurallarına göre hesaplanır.
      </p>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="mb-3 d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
              const r = buildPresetRange(1); setFrom(r.from); setTo(r.to);
            }}>Bugün</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
              const r = buildPresetRange(7); setFrom(r.from); setTo(r.to);
            }}>Son 7 gün</button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => {
              const r = buildPresetRange(30); setFrom(r.from); setTo(r.to);
            }}>Son 30 gün</button>
          </div>
          <form
            className="row g-3 align-items-end"
            onSubmit={(e) => {
              e.preventDefault();
              void loadAll();
            }}
          >
            <div className="col-md-3">
              <label className="form-label">Başlangıç</label>
              <input type="date" className="form-control" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Bitiş</label>
              <input type="date" className="form-control" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Oturum tipi</label>
              <select
                className="form-select"
                value={authFilter}
                onChange={(e) => setAuthFilter(e.target.value as AnalyticsAuthFilter)}
              >
                <option value="All">Tümü</option>
                <option value="Anonymous">Anonim</option>
                <option value="Authenticated">Giriş yapmış</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Sayfa tipi (opsiyonel)</label>
              <input
                className="form-control"
                placeholder="listing_detail"
                value={pageTypeFilter}
                onChange={(e) => setPageTypeFilter(e.target.value)}
              />
            </div>
            <div className="col-12">
              <button type="submit" className="btn btn-primary" disabled={loading || rangeInvalid}>
                {loading ? 'Yükleniyor…' : 'Uygula'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {overview && (
        <>
          <div className="row g-3 mb-4">
            {[
              { label: 'Sayfa görüntüleme', value: overview.totalPageViews },
              { label: 'Benzersiz ziyaretçi', value: overview.uniqueVisitors },
              { label: 'Oturum', value: overview.totalSessions },
              { label: 'Ort. etkileşim / sayfa', value: formatSeconds(overview.avgEngagedSecondsPerPage) },
              { label: 'Ort. oturum süresi', value: formatSeconds(overview.avgSessionDurationSeconds) },
              { label: 'Ort. sayfa / oturum', value: overview.avgPagesPerSession.toFixed(2) },
              {
                label: 'Hemen çıkma oranı',
                value: `${(overview.bounceRate * 100).toFixed(1)}%`,
              },
            ].map((card) => (
              <div className="col-md-4 col-lg-2" key={card.label}>
                <div className="card shadow-sm h-100">
                  <div className="card-body p-3">
                    <div className="small text-muted">{card.label}</div>
                    <div className="fw-bold fs-5">{typeof card.value === 'number' ? card.value.toLocaleString('tr-TR') : card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="small text-muted">{overview.bounceRateDefinition}</p>

          <div className="card shadow-sm mb-4">
            <div className="card-header">Sayfa görüntüleme trendi</div>
            <div className="card-body" style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={overview.pageViewsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#0d6efd" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {pages.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-header">Sayfa performansı</div>
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>Sayfa</th>
                  <th className="text-end">Görüntüleme</th>
                  <th className="text-end">Ziyaretçi</th>
                  <th className="text-end">Oturum</th>
                  <th className="text-end">Ort. etkileşim</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((row) => (
                  <tr key={`${row.pageType}-${row.normalizedPath}`}>
                    <td>
                      <div className="fw-semibold">{row.pageLabel}</div>
                      <div className="small text-muted">{row.normalizedPath}</div>
                    </td>
                    <td className="text-end">{row.views.toLocaleString('tr-TR')}</td>
                    <td className="text-end">{row.uniqueVisitors.toLocaleString('tr-TR')}</td>
                    <td className="text-end">{row.sessions.toLocaleString('tr-TR')}</td>
                    <td className="text-end">{formatSeconds(row.avgEngagedSeconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {journeys.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-header">Popüler yolculuklar (3 adım)</div>
          <ul className="list-group list-group-flush">
            {journeys.map((j) => (
              <li className="list-group-item d-flex justify-content-between" key={j.journeyLabel}>
                <span>{j.journeyLabel}</span>
                <span className="text-muted">
                  {j.sessionCount} oturum ({(j.shareOfSessions * 100).toFixed(1)}%)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sessions.length > 0 && (
        <div className="card shadow-sm mb-4">
          <div className="card-header d-flex justify-content-between">
            <span>Oturum gezgini</span>
            <span className="small text-muted">{sessionTotal} oturum</span>
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Başlangıç</th>
                  <th>Sayfa</th>
                  <th>Etkileşim</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.sessionId}>
                    <td><code>{s.sessionRef}</code></td>
                    <td>{new Date(s.startedAt).toLocaleString('tr-TR')}</td>
                    <td>{s.pageViewCount}</td>
                    <td>{formatSeconds(s.totalEngagedMs / 1000)}</td>
                    <td>
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => void openSession(s.sessionId)}>
                        İncele
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSession && (
        <div className="card shadow-sm mb-4">
          <div className="card-header">
            Oturum {selectedSession.summary.sessionRef}
          </div>
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Zaman</th>
                  <th>Sayfa</th>
                  <th className="text-end">Etkileşim</th>
                </tr>
              </thead>
              <tbody>
                {selectedSession.pages.map((p) => (
                  <tr key={p.sequenceNumber}>
                    <td>{new Date(p.viewedAt).toLocaleTimeString('tr-TR')}</td>
                    <td>{p.pageLabel}</td>
                    <td className="text-end">{formatSeconds(p.engagedMs / 1000)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
